<template>
  <!-- Add/Edit Deals Modal (UPGRADED UI ONLY) -->
  <div class="modal fade dq-deals-modal" id="add_deals" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered modal-xl dq-deals-dialog">
      <div class="modal-content dq-modal-content" :key="keyForce">
        <!-- Header -->
        <div class="modal-header dq-modal-header">
          <div class="dq-titlewrap">
            <div class="dq-hero dq-hero-emerald">
              <i class="ti ti-briefcase"></i>
            </div>
            <div class="dq-headtext">
              <h4 class="modal-title dq-title mb-0">
                {{ isEdit ? "Edit Deal" : "Add New Deal" }}
              </h4>
              <div class="dq-subtitle">
                Track pipeline stage, value, ownership, and visibility.
              </div>
            </div>
          </div>

          <div class="dq-head-actions">
            <span class="dq-pill">
              <i class="ti ti-chart-dots-3"></i>
              <span class="dq-pill-label">Status</span>
              <span class="dq-pill-value">{{ form.status || "Open" }}</span>
            </span>

            <button
              type="button"
              class="btn-close custom-btn-close dq-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            >
              <i class="ti ti-x"></i>
            </button>
          </div>
        </div>

        <form @submit.prevent="submit">
          <!-- Body -->
          <div class="modal-body dq-modal-body">
            <div class="row g-3">
              <!-- Deal Summary Card -->
              <div class="col-12">
                <div class="dq-card">
                  <div class="dq-card-head">
                    <div class="dq-card-title">
                      <i class="ti ti-sparkles"></i>
                      Deal Summary
                    </div>
                    <div class="dq-card-sub">
                      Required: Deal Name. Optional: Amount, Probability and
                      Dates.
                    </div>
                  </div>

                  <div class="row g-3 mt-0">
                    <!-- Deal name -->
                    <div class="col-12">
                      <label class="form-label dq-label">
                        Deal Name <span class="text-danger">*</span>
                      </label>
                      <input
                        v-model.trim="form.name"
                        type="text"
                        class="form-control dq-input"
                        placeholder="e.g. HRMS Implementation - Example Ltd"
                        required
                      />
                    </div>

                    <!-- Pipeline / Stage -->
                    <!-- Pipeline -->
                    <div class="col-md-6">
                      <label class="form-label dq-label">Pipeline</label>

                      <!-- Selected -->
                      <div class="mb-2">
                        <span
                          v-if="form.pipeline_id"
                          class="badge bg-light text-dark"
                        >
                          {{ getPipelineLabel(form.pipeline_id) }}
                          <i
                            class="ti ti-x ms-1"
                            role="button"
                            @click="clearPipeline"
                          ></i>
                        </span>
                        <span v-else class="text-muted"
                          >No pipeline selected</span
                        >
                      </div>

                      <!-- Dropdown -->
                      <div
                        class="dropdown w-100"
                        data-bs-auto-close="outside"
                        data-bs-display="static"
                      >
                        <button
                          class="btn btn-outline-secondary w-100 d-flex justify-content-between align-items-center"
                          type="button"
                          data-bs-toggle="dropdown"
                          @click="openPipelineDropdown"
                        >
                          <span class="text-truncate">
                            {{
                              form.pipeline_id
                                ? "Change pipeline…"
                                : "Search pipeline…"
                            }}
                          </span>
                          <i class="ti ti-chevron-down ms-2"></i>
                        </button>

                        <div class="dropdown-menu p-2 w-100 shadow">
                          <input
                            v-model="pipelineQuery"
                            @input="onPipelineInput"
                            type="text"
                            class="form-control mb-2"
                            placeholder="Search pipelines…"
                            autocomplete="off"
                          />

                          <div class="search-list">
                            <div
                              v-if="pipelineLoading"
                              class="text-center py-2 small text-muted"
                            >
                              Loading…
                            </div>

                            <button
                              v-for="opt in pipelineOptions"
                              :key="'pipe-' + opt.id"
                              type="button"
                              class="dropdown-item d-flex justify-content-between align-items-center"
                              @click="selectPipeline(opt)"
                            >
                              <span class="text-truncate">{{ opt.label }}</span>
                              <i
                                class="ti"
                                :class="
                                  form.pipeline_id === opt.id
                                    ? 'ti-check'
                                    : 'ti-forms'
                                "
                              ></i>
                            </button>

                            <div
                              v-if="!pipelineLoading && !pipelineOptions.length"
                              class="text-center py-2 small text-muted"
                            >
                              No results
                            </div>
                          </div>
                        </div>
                      </div>

                      <div class="dq-help" v-if="!form.pipeline_id">
                        Pick a pipeline to load its stages.
                      </div>
                    </div>

                    <!-- Stage -->
                    <div class="col-md-6">
                      <label class="form-label dq-label">Stage</label>

                      <!-- Selected -->
                      <div class="mb-2">
                        <span
                          v-if="form.stage_id"
                          class="badge bg-light text-dark"
                        >
                          {{ getStageLabel(form.stage_id) }}
                          <i
                            class="ti ti-x ms-1"
                            role="button"
                            @click="form.stage_id = null"
                          ></i>
                        </span>
                        <span v-else class="text-muted">No stage selected</span>
                      </div>

                      <!-- Dropdown -->
                      <div
                        class="dropdown w-100"
                        data-bs-auto-close="outside"
                        data-bs-display="static"
                      >
                        <button
                          class="btn btn-outline-secondary w-100 d-flex justify-content-between align-items-center"
                          type="button"
                          data-bs-toggle="dropdown"
                          :disabled="!form.pipeline_id"
                          @click="openStageDropdown"
                        >
                          <span class="text-truncate">
                            {{
                              form.pipeline_id
                                ? "Search stage…"
                                : "Select pipeline first"
                            }}
                          </span>
                          <i class="ti ti-chevron-down ms-2"></i>
                        </button>

                        <div class="dropdown-menu p-2 w-100 shadow">
                          <input
                            v-model="stageQuery"
                            @input="onStageInput"
                            type="text"
                            class="form-control mb-2"
                            placeholder="Search stages…"
                            autocomplete="off"
                            :disabled="!form.pipeline_id"
                          />

                          <div class="search-list">
                            <div
                              v-if="stageLoading"
                              class="text-center py-2 small text-muted"
                            >
                              Loading…
                            </div>

                            <button
                              v-for="opt in stageDropdownOptions"
                              :key="'stage-' + opt.id"
                              type="button"
                              class="dropdown-item d-flex justify-content-between align-items-center"
                              @click="selectStage(opt)"
                            >
                              <span class="text-truncate">{{ opt.label }}</span>
                              <i
                                class="ti"
                                :class="
                                  form.stage_id === opt.id
                                    ? 'ti-check'
                                    : 'ti-git-branch'
                                "
                              ></i>
                            </button>

                            <div
                              v-if="
                                !stageLoading && !stageDropdownOptions.length
                              "
                              class="text-center py-2 small text-muted"
                            >
                              No results
                            </div>
                          </div>
                        </div>
                      </div>

                      <div
                        class="dq-help"
                        v-if="form.pipeline_id && !stageOptions.length"
                      >
                        No stages found for this pipeline.
                      </div>
                    </div>

                    <!-- Amount / Currency / Probability -->
                    <div class="col-md-4">
                      <label class="form-label dq-label">Amount</label>
                      <div class="dq-inputgroup">
                        <span class="dq-addon"
                          ><i class="ti ti-currency-dollar"></i
                        ></span>
                        <input
                          v-model.number="form.amount"
                          type="number"
                          min="0"
                          step="0.01"
                          class="form-control dq-input dq-inputgroup-input"
                          placeholder="0.00"
                        />
                      </div>
                    </div>

                    <div class="col-md-4">
                      <label class="form-label dq-label">Currency</label>
                      <select
                        v-model="form.currency"
                        class="form-select dq-input"
                      >
                        <option value="KES">KES</option>
                        <option value="UGX">UGX</option>
                        <option value="TZS">TZS</option>
                        <option value="USD">USD</option>
                        <option value="EUR">EUR</option>
                        <option value="GBP">GBP</option>
                      </select>
                    </div>

                    <div class="col-md-4">
                      <label class="form-label dq-label">Probability (%)</label>
                      <div class="dq-inputgroup">
                        <span class="dq-addon"
                          ><i class="ti ti-percentage"></i
                        ></span>
                        <input
                          v-model.number="form.probability"
                          type="number"
                          min="0"
                          max="100"
                          step="1"
                          class="form-control dq-input dq-inputgroup-input"
                          placeholder="e.g. 60"
                        />
                      </div>
                      <div
                        class="dq-meter"
                        v-if="
                          form.probability !== null && form.probability !== ''
                        "
                      >
                        <div
                          class="dq-meter-bar"
                          :style="{
                            width:
                              Math.min(
                                100,
                                Math.max(0, Number(form.probability || 0))
                              ) + '%',
                          }"
                        ></div>
                      </div>
                    </div>

                    <!-- Dates -->
                    <div class="col-md-6">
                      <label class="form-label dq-label"
                        >Expected Close Date</label
                      >
                      <input
                        v-model="form.expected_close_date"
                        type="date"
                        class="form-control dq-input"
                      />
                    </div>

                    <div class="col-md-6">
                      <label class="form-label dq-label">Next Follow-up</label>
                      <input
                        v-model="form.next_follow_up"
                        type="date"
                        class="form-control dq-input"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <!-- Ownership & Outcome -->
              <div class="col-lg-6">
                <div class="dq-card">
                  <div class="dq-card-head">
                    <div class="dq-card-title">
                      <i class="ti ti-user-check"></i>
                      Ownership & Outcome
                    </div>
                    <div class="dq-card-sub">
                      Assign owner, status and priority.
                    </div>
                  </div>

                  <div class="row g-3 mt-0">
                    <div class="col-md-6">
                      <div class="mb-3">
                        <label class="form-label">Status</label>
                        <select v-model="form.status" class="form-select">
                          <option>Open</option>
                          <option>Won</option>
                          <option>Lost</option>
                        </select>
                      </div>
                    </div>
                    <div class="col-md-6">
                      <div class="mb-3">
                        <label class="form-label">Priority</label>
                        <select v-model="form.priority" class="form-select">
                          <option :value="null">Select</option>
                          <option>Low</option>
                          <option>Medium</option>
                          <option>High</option>
                        </select>
                      </div>
                    </div>

                    <!-- Owner -->
                    <div class="col-12">
                      <div class="mb-0">
                        <label class="form-label dq-label">Owner</label>

                        <!-- Selected -->
                        <div class="mb-2">
                          <span
                            v-if="form.owner_id"
                            class="badge bg-light text-dark"
                          >
                            {{ getOwnerLabel(form.owner_id) }}
                            <i
                              class="ti ti-x ms-1"
                              role="button"
                              @click="form.owner_id = null"
                            ></i>
                          </span>
                          <span v-else class="text-muted"
                            >No owner selected</span
                          >
                        </div>

                        <!-- Dropdown -->
                        <div
                          class="dropdown w-100"
                          data-bs-auto-close="outside"
                          data-bs-display="static"
                        >
                          <button
                            class="btn btn-outline-secondary w-100 d-flex justify-content-between align-items-center"
                            type="button"
                            data-bs-toggle="dropdown"
                            @click="openOwnerDropdown"
                          >
                            <span class="text-truncate">
                              {{
                                form.owner_id
                                  ? "Change owner…"
                                  : "Search owner…"
                              }}
                            </span>
                            <i class="ti ti-chevron-down ms-2"></i>
                          </button>

                          <div class="dropdown-menu p-2 w-100 shadow">
                            <input
                              v-model="ownerQuery"
                              @input="onOwnerInput"
                              type="text"
                              class="form-control mb-2"
                              placeholder="Search users…"
                              autocomplete="off"
                            />

                            <div class="search-list">
                              <div
                                v-if="ownerLoading"
                                class="text-center py-2 small text-muted"
                              >
                                Loading…
                              </div>

                              <button
                                v-for="opt in ownerOptions"
                                :key="'owner-' + opt.id"
                                type="button"
                                class="dropdown-item d-flex justify-content-between align-items-center"
                                @click="selectOwner(opt)"
                              >
                                <span class="text-truncate">{{
                                  opt.label
                                }}</span>
                                <i
                                  class="ti"
                                  :class="
                                    form.owner_id === opt.id
                                      ? 'ti-check'
                                      : 'ti-user'
                                  "
                                ></i>
                              </button>

                              <div
                                v-if="!ownerLoading && !ownerOptions.length"
                                class="text-center py-2 small text-muted"
                              >
                                No results
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div class="col-12">
                      <label class="form-label dq-label">Account</label>
                      <select v-model="form.client_id" class="form-select dq-input mb-2">
                        <option :value="null">Select account</option>
                        <option
                          v-for="client in clients"
                          :key="client.id"
                          :value="client.id"
                        >
                          {{ client.name }}
                        </option>
                      </select>
                      <input
                        v-model.trim="form.company_name"
                        type="text"
                        class="form-control dq-input"
                        :readonly="!!form.client_id"
                        :placeholder="
                          form.client_id
                            ? 'Synced from selected account'
                            : 'e.g. Example Technologies'
                        "
                      />
                    </div>
                  </div>
                </div>
              </div>

              <!-- Contact & Source -->
              <div class="col-lg-6">
                <div class="dq-card">
                  <div class="dq-card-head">
                    <div class="dq-card-title">
                      <i class="ti ti-address-book"></i>
                      Contact & Source
                    </div>
                    <div class="dq-card-sub">
                      Optional details for quick follow-up.
                    </div>
                  </div>

                  <div class="row g-3 mt-0">
                    <div class="col-md-4">
                      <label class="form-label dq-label">Contact Name</label>
                      <input
                        v-model.trim="form.contact_name"
                        type="text"
                        class="form-control dq-input"
                        placeholder="e.g. Jane Doe"
                      />
                    </div>

                    <div class="col-md-4">
                      <label class="form-label dq-label">Contact Email</label>
                      <input
                        v-model.trim="form.contact_email"
                        type="email"
                        class="form-control dq-input"
                        placeholder="jane@company.com"
                      />
                    </div>

                    <div class="col-md-4">
                      <label class="form-label dq-label">Contact Phone</label>
                      <input
                        v-model.trim="form.contact_phone"
                        type="text"
                        class="form-control dq-input"
                        placeholder="+2547..."
                      />
                    </div>

                    <div class="col-12">
                      <label class="form-label dq-label">Source</label>
                      <input
                        v-model.trim="form.source"
                        class="form-control dq-input"
                        placeholder="Phone, Social Media, Referral..."
                      />
                    </div>

                    <div class="col-12">
                      <label class="form-label dq-label d-block">Tags</label>
                      <div class="dq-tags">
                        <label class="dq-tagcheck">
                          <input
                            class="form-check-input"
                            type="checkbox"
                            value="Promotion"
                            v-model="form.tags"
                          />
                          <span>Promotion</span>
                        </label>
                        <label class="dq-tagcheck">
                          <input
                            class="form-check-input"
                            type="checkbox"
                            value="Rated"
                            v-model="form.tags"
                          />
                          <span>Rated</span>
                        </label>
                        <label class="dq-tagcheck">
                          <input
                            class="form-check-input"
                            type="checkbox"
                            value="Collab"
                            v-model="form.tags"
                          />
                          <span>Collab</span>
                        </label>
                        <label class="dq-tagcheck">
                          <input
                            class="form-check-input"
                            type="checkbox"
                            value="Rejected"
                            v-model="form.tags"
                          />
                          <span>Rejected</span>
                        </label>
                      </div>

                      <div v-if="form.tags?.length" class="dq-selected-tags">
                        <span class="dq-mini">Selected:</span>
                        <span v-for="t in form.tags" :key="t" class="dq-chip">
                          <i class="ti ti-tag"></i> {{ t }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Description -->
              <div class="col-12">
                <div class="dq-card">
                  <div class="dq-card-head">
                    <div class="dq-card-title">
                      <i class="ti ti-notes"></i>
                      Notes
                    </div>
                    <div class="dq-card-sub">Add context for the team.</div>
                  </div>

                  <div class="mt-2">
                    <label class="form-label dq-label">Description</label>
                    <textarea
                      v-model.trim="form.description"
                      class="form-control dq-input dq-textarea"
                      rows="4"
                      placeholder="Write a short summary of this opportunity..."
                    ></textarea>
                  </div>
                </div>
              </div>

              <!-- Visibility -->
              <div class="col-12">
                <div class="dq-card">
                  <div class="dq-card-head">
                    <div class="dq-card-title">
                      <i class="ti ti-eye"></i>
                      Visibility
                    </div>
                    <div class="dq-card-sub">
                      Control who can view this deal.
                    </div>
                  </div>

                  <div class="dq-visibility">
                    <label class="dq-radio">
                      <input
                        class="form-check-input"
                        type="radio"
                        value="public"
                        v-model="form.visibility"
                      />
                      <span class="dq-radio-box">
                        <i class="ti ti-world"></i>
                        <span class="dq-radio-title">Public</span>
                        <!-- <span class="dq-radio-sub">Visible to everyone in the company.</span> -->
                      </span>
                    </label>

                    <label class="dq-radio">
                      <input
                        class="form-check-input"
                        type="radio"
                        value="private"
                        v-model="form.visibility"
                      />
                      <span class="dq-radio-box">
                        <i class="ti ti-lock"></i>
                        <span class="dq-radio-title">Private</span>
                        <!-- <span class="dq-radio-sub">Visible only to the owner/admins.</span> -->
                      </span>
                    </label>

                    <label class="dq-radio">
                      <input
                        class="form-check-input"
                        type="radio"
                        value="restricted"
                        v-model="form.visibility"
                      />
                      <span class="dq-radio-box">
                        <i class="ti ti-users"></i>
                        <span class="dq-radio-title">Select People</span>
                        <!-- <span class="dq-radio-sub">Visible to selected users.</span> -->
                      </span>
                    </label>
                  </div>

                  <!-- Restricted people picker (Searchable + multi-select) -->
                  <div
                    class="dq-restrict mt-3"
                    v-if="form.visibility === 'restricted'"
                  >
                    <div class="dq-restrict-head">
                      <div class="dq-restrict-title">
                        <i class="ti ti-user-plus"></i> Choose people
                      </div>
                      <div class="dq-restrict-sub">
                        Select one or more users who can view this deal.
                      </div>
                    </div>

                    <!-- Selected users -->
                    <div
                      v-if="restrictedSelected.length"
                      class="dq-selected mt-2"
                    >
                      <span class="dq-mini">Selected:</span>

                      <span
                        v-for="u in restrictedSelected"
                        :key="'sel-' + u.id"
                        class="dq-chip"
                      >
                        <i class="ti ti-user"></i>
                        {{ u.label }}
                        <i
                          class="ti ti-x ms-1"
                          role="button"
                          @click="removeRestricted(u.id)"
                        ></i>
                      </span>
                    </div>

                    <!-- Dropdown -->
                    <div
                      class="dropdown w-100 mt-2"
                      data-bs-auto-close="outside"
                      data-bs-display="static"
                    >
                      <button
                        class="btn btn-outline-secondary w-100 d-flex justify-content-between align-items-center"
                        type="button"
                        data-bs-toggle="dropdown"
                        @click="openRestrictedDropdown"
                      >
                        <span class="text-truncate"> Add people… </span>
                        <i class="ti ti-chevron-down ms-2"></i>
                      </button>

                      <div class="dropdown-menu p-2 w-100 shadow">
                        <input
                          v-model="restrictedQuery"
                          type="text"
                          class="form-control mb-2"
                          placeholder="Search users…"
                          autocomplete="off"
                        />

                        <div class="search-list">
                          <div
                            v-if="restrictedLoading"
                            class="text-center py-2 small text-muted"
                          >
                            Loading…
                          </div>

                          <button
                            v-for="opt in restrictedOptions"
                            :key="'rest-' + opt.id"
                            type="button"
                            class="dropdown-item d-flex justify-content-between align-items-center"
                            @click="toggleRestricted(opt)"
                          >
                            <span class="text-truncate">{{ opt.label }}</span>
                            <i
                              class="ti"
                              :class="
                                form.user_ids.includes(opt.id)
                                  ? 'ti-check'
                                  : 'ti-user-plus'
                              "
                            ></i>
                          </button>

                          <div
                            v-if="
                              !restrictedLoading && !restrictedOptions.length
                            "
                            class="text-center py-2 small text-muted"
                          >
                            No results
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <!-- /Visibility -->
            </div>
          </div>

          <!-- Footer (sticky, premium) -->
          <div class="modal-footer dq-modal-footer">
            <button
              type="button"
              class="btn btn-light dq-btn dq-btn-muted"
              data-bs-dismiss="modal"
              :disabled="dealStore.loading"
            >
              Cancel
            </button>

            <button
              type="submit"
              class="btn btn-primary dq-btn dq-btn-primary"
              :disabled="dealStore.loading"
            >
              <span
                v-if="dealStore.loading"
                class="spinner-border spinner-border-sm me-2"
              ></span>
              {{ isEdit ? "Save Changes" : "Add Deal" }}
              <i class="ti ti-arrow-right ms-2"></i>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <!-- Delete Deals Modal (UPGRADED UI ONLY) -->
  <div class="modal fade dq-deals-modal" id="delete_deals" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered modal-sm dq-del-dialog">
      <div class="modal-content dq-modal-content">
        <div class="modal-header dq-modal-header dq-danger-head">
          <div class="dq-titlewrap">
            <div class="dq-hero dq-hero-red">
              <i class="ti ti-trash"></i>
            </div>
            <div>
              <h5 class="modal-title dq-title mb-0">Confirm Delete</h5>
              <div class="dq-subtitle">This action cannot be undone.</div>
            </div>
          </div>

          <button
            type="button"
            class="btn-close custom-btn-close dq-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>

        <div class="modal-body dq-modal-body">
          <div class="dq-danger">
            <i class="ti ti-alert-octagon"></i>
            <div>
              <div class="dq-danger-title">Delete this deal record?</div>
              <div class="dq-danger-sub">
                Reports and pipeline metrics may change after deletion.
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer dq-modal-footer">
          <button
            type="button"
            class="btn btn-light dq-btn dq-btn-muted"
            data-bs-dismiss="modal"
            :disabled="dealStore.loading"
          >
            Cancel
          </button>

          <button
            type="button"
            class="btn btn-danger dq-btn dq-btn-danger"
            :disabled="dealStore.loading"
            @click="confirmDelete"
          >
            <span
              v-if="dealStore.loading"
              class="spinner-border spinner-border-sm me-2"
            ></span>
            Delete
            <i class="ti ti-trash ms-2"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import Modal from "bootstrap/js/dist/modal";
import { toast } from "@/utils/toast";
import { useCrmClientStore } from "@/stores/crmClientStore";
import { useDealStore } from "@/stores/dealStore";
import { usePipelineStore } from "@/stores/pipelineStore";
import { useUserStore } from "@/stores/userStore";

const props = defineProps({ editItem: { type: Object, default: null } });
const emit = defineEmits(["saved"]);

const dealStore = useDealStore();
const clientStore = useCrmClientStore();
const pipeStore = usePipelineStore();
const userStore = useUserStore();

const keyForce = ref(0);
const isEdit = computed(() => !!props.editItem?.id);

const form = ref(blankForm());

function blankForm() {
  return {
    id: null,
    name: "",
    status: "Open",
    client_id: null,
    pipeline_id: null,
    stage_id: null,
    owner_id: null,
    amount: 0,
    currency: "KES",
    probability: null,
    expected_close_date: "",
    next_follow_up: "",
    priority: null,
    company_name: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    source: "",
    tags: [],
    description: "",
    visibility: "public",
    user_ids: [],
  };
}
function reset() {
  Object.assign(form.value, blankForm());
  keyForce.value++;
}

const pipelines = computed(() => pipeStore.items || []);
const selectedPipeline = computed(
  () => pipelines.value.find((p) => p.id === form.value.pipeline_id) || null
);
const stageOptions = computed(() => selectedPipeline.value?.stages || []);
const users = computed(() =>
  Array.isArray(userStore.users)
    ? userStore.users
    : Array.isArray(userStore.users?.data)
    ? userStore.users.data
    : []
);
const clients = computed(() =>
  Array.isArray(clientStore.clients) ? clientStore.clients : []
);

function onPipelineChange() {
  if (!stageOptions.value.find((s) => s.id === form.value.stage_id)) {
    form.value.stage_id = stageOptions.value[0]?.id ?? null;
  }
}
watch(() => form.value.pipeline_id, onPipelineChange);

function nameById(id) {
  const u = users.value.find((x) => Number(x.id) === Number(id));
  return u
    ? u.first_name
      ? `${u.first_name} ${u.last_name || ""}`
      : u.email
    : `User ${id}`;
}

function toNumOrNull(v) {
  return v === "" || v === null || v === undefined ? null : Number(v);
}
function toNumArray(a) {
  return Array.isArray(a)
    ? a.map((n) => Number(n)).filter((n) => Number.isFinite(n))
    : [];
}

// -------------------------
// Searchable dropdowns (Pipeline / Stage / Owner)
// -------------------------

const pipelineQuery = ref("");
const stageQuery = ref("");
const ownerQuery = ref("");

const pipelineLoading = ref(false);
const stageLoading = ref(false);
const ownerLoading = ref(false);

// Build options (client-side filtering from loaded stores)
const pipelineOptions = computed(() => {
  const q = pipelineQuery.value.trim().toLowerCase();
  const list = (pipelines.value || []).map((p) => ({
    id: Number(p.id),
    label: p.name,
  }));
  return q ? list.filter((x) => x.label.toLowerCase().includes(q)) : list;
});

const stageDropdownOptions = computed(() => {
  const q = stageQuery.value.trim().toLowerCase();
  const list = (stageOptions.value || []).map((s) => ({
    id: Number(s.id),
    label: s.name,
  }));
  return q ? list.filter((x) => x.label.toLowerCase().includes(q)) : list;
});

const ownerOptions = computed(() => {
  const q = ownerQuery.value.trim().toLowerCase();
  const list = (users.value || []).map((u) => ({
    id: Number(u.id),
    label: u.first_name
      ? `${u.first_name} ${u.last_name || ""}`.trim()
      : u.email,
  }));
  return q ? list.filter((x) => x.label.toLowerCase().includes(q)) : list;
});

// Labels (used by selected badge)
function getPipelineLabel(id) {
  const p = (pipelines.value || []).find((x) => Number(x.id) === Number(id));
  return p?.name || `Pipeline ${id}`;
}
function getStageLabel(id) {
  const s = (stageOptions.value || []).find((x) => Number(x.id) === Number(id));
  return s?.name || `Stage ${id}`;
}
function getOwnerLabel(id) {
  return nameById(id);
}

// Dropdown open hooks (ensure data is loaded + focus behavior if needed)
async function openPipelineDropdown() {
  pipelineLoading.value = true;
  try {
    if (!pipeStore.items?.length) await pipeStore.ensureAll({ per_page: 200 });
  } finally {
    pipelineLoading.value = false;
  }
}
async function openStageDropdown() {
  stageLoading.value = true;
  try {
    // stages are embedded in selectedPipeline; nothing extra to fetch here
  } finally {
    stageLoading.value = false;
  }
}
async function openOwnerDropdown() {
  ownerLoading.value = true;
  try {
    if (!userStore.users?.length) await userStore.ensureUsers({ per_page: 200 });
  } finally {
    ownerLoading.value = false;
  }
}

// Input handlers (kept for parity with your pattern)
function onPipelineInput() {}
function onStageInput() {}
function onOwnerInput() {}

// Select handlers
function selectPipeline(opt) {
  form.value.pipeline_id = Number(opt.id);
  // reset stage if not valid
  if (
    !stageOptions.value.find(
      (s) => Number(s.id) === Number(form.value.stage_id)
    )
  ) {
    form.value.stage_id = stageOptions.value[0]?.id ?? null;
  }
}
function selectStage(opt) {
  form.value.stage_id = Number(opt.id);
}
function selectOwner(opt) {
  form.value.owner_id = Number(opt.id);
}

// Clears
function clearPipeline() {
  form.value.pipeline_id = null;
  form.value.stage_id = null;
  pipelineQuery.value = "";
  stageQuery.value = "";
}

// -------------------------
// Restricted users picker
// -------------------------

const restrictedQuery = ref("");
const restrictedLoading = ref(false);

// Build searchable options
const restrictedOptions = computed(() => {
  const q = restrictedQuery.value.trim().toLowerCase();
  return users.value
    .map((u) => ({
      id: Number(u.id),
      label: u.first_name
        ? `${u.first_name} ${u.last_name || ""}`.trim()
        : u.email,
    }))
    .filter((x) => (q ? x.label.toLowerCase().includes(q) : true));
});

// Selected user objects (for chips)
const restrictedSelected = computed(() =>
  restrictedOptions.value.filter((x) => form.value.user_ids.includes(x.id))
);

// Dropdown open
async function openRestrictedDropdown() {
  restrictedLoading.value = true;
  try {
    if (!userStore.users?.length) {
      await userStore.ensureUsers({ per_page: 200 });
    }
  } finally {
    restrictedLoading.value = false;
  }
}

// Toggle selection
function toggleRestricted(opt) {
  const idx = form.value.user_ids.indexOf(opt.id);
  if (idx === -1) {
    form.value.user_ids.push(opt.id);
  } else {
    form.value.user_ids.splice(idx, 1);
  }
}

// Remove from selected chips
function removeRestricted(id) {
  form.value.user_ids = form.value.user_ids.filter(
    (x) => Number(x) !== Number(id)
  );
}

/** Hydrate for edit */
function hydrate(val) {
  if (!val) {
    reset();
    return;
  }

  form.value.id = val.id ?? null;
  form.value.name = val.name ?? "";
  form.value.status = val.status ?? "Open";
  form.value.client_id = toNumOrNull(val.client_id ?? val.client?.id);
  form.value.pipeline_id = toNumOrNull(val.pipeline_id);
  form.value.stage_id = toNumOrNull(val.stage_id);
  form.value.owner_id = toNumOrNull(val.owner_id);
  form.value.amount = Number(val.amount ?? 0);
  form.value.currency = val.currency ?? "KES";
  form.value.probability = val.probability ?? null;
  form.value.expected_close_date = val.expected_close_date ?? "";
  form.value.next_follow_up = val.next_follow_up ?? "";
  form.value.priority = val.priority ?? null;
  form.value.company_name = val.company_name ?? "";
  form.value.contact_name = val.contact_name ?? "";
  form.value.contact_email = val.contact_email ?? "";
  form.value.contact_phone = val.contact_phone ?? "";
  form.value.source = val.source ?? "";
  form.value.tags = Array.isArray(val.tags) ? [...val.tags] : [];
  form.value.description = val.description ?? "";
  form.value.visibility = val.visibility ?? "public";

  const idsFromArray = toNumArray(val.user_ids);
  const idsFromObjects = Array.isArray(val.users)
    ? toNumArray(val.users.map((u) => u.id))
    : [];
  form.value.user_ids = [...new Set([...idsFromArray, ...idsFromObjects])];

  if (form.value.user_ids.length && form.value.visibility !== "restricted") {
    form.value.visibility = "restricted";
  }
  if (!stageOptions.value.find((s) => Number(s.id) === form.value.stage_id)) {
    form.value.stage_id = stageOptions.value[0]?.id ?? null;
  }
  keyForce.value++;
}

watch(
  () => props.editItem,
  async (val) => {
    if (!clientStore.clients?.length) await clientStore.ensureClients({ per_page: 200 });
    if (!pipeStore.items?.length) await pipeStore.ensureAll({ per_page: 200 });
    if (!userStore.users?.length) await userStore.ensureUsers({ per_page: 200 });
    hydrate(val);
  },
  { immediate: true }
);

watch(
  () => form.value.client_id,
  (clientId) => {
    const selected = clients.value.find((item) => Number(item.id) === Number(clientId));
    if (selected) {
      form.value.company_name = selected.name || "";
    }
  }
);

function hide() {
  const el = document.getElementById("add_deals");
  const inst = el
    ? Modal.getInstance(el) || Modal.getOrCreateInstance(el)
    : null;
  inst?.hide();
}

function toPayload(f) {
  return {
    name: f.name?.trim(),
    status: f.status || "Open",
    client_id: f.client_id || null,
    pipeline_id: f.pipeline_id || null,
    stage_id: f.stage_id || null,
    owner_id: f.owner_id || null,
    amount: Number.isFinite(f.amount) ? f.amount : 0,
    currency: f.currency || "KES",
    probability: f.probability ?? null,
    expected_close_date: f.expected_close_date || null,
    next_follow_up: f.next_follow_up || null,
    priority: f.priority || null,
    company_name: f.company_name?.trim() || null,
    contact_name: f.contact_name?.trim() || null,
    contact_email: f.contact_email?.trim() || null,
    contact_phone: f.contact_phone?.trim() || null,
    source: f.source?.trim() || null,
    tags: Array.isArray(f.tags) ? f.tags : [],
    description: f.description?.trim() || null,
    visibility: f.visibility || "public",
    user_ids: f.visibility === "restricted" ? [...f.user_ids] : [],
  };
}

async function submit() {
  if (!form.value.name?.trim()) return toast("Deal name is required", "error");
  if (form.value.visibility === "restricted" && !form.value.user_ids.length) {
    return toast(
      "Select at least one user or choose a different visibility",
      "error"
    );
  }
  const payload = toPayload(form.value);
  try {
    if (isEdit.value && form.value.id) {
      await dealStore.update(form.value.id, payload);
    } else {
      await dealStore.create(payload);
    }
    hide();
    emit("saved");
  } catch (e) {
    toast(e?.response?.data?.message || "Failed to save deal", "error");
  }
}

function hideDelete() {
  const el = document.getElementById("delete_deals");
  const inst = el
    ? Modal.getInstance(el) || Modal.getOrCreateInstance(el)
    : null;
  inst?.hide();
}

async function confirmDelete() {
  try {
    if (!props.editItem?.id) return;
    await dealStore.remove(props.editItem.id);
    hideDelete();
    emit("saved");
  } catch (e) {
    console.error(e);
  }
}

onMounted(async () => {
  if (!pipeStore.items?.length) await pipeStore.ensureAll({ per_page: 200 });
  if (!userStore.users?.length) await userStore.ensureUsers({ per_page: 200 });

  const el = document.getElementById("add_deals");
  el?.addEventListener("show.bs.modal", () => {
    if (isEdit.value) hydrate(props.editItem);
    else reset();
  });
  el?.addEventListener("hidden.bs.modal", reset);
});
</script>

<style scoped>
/* Wider + premium */
.dq-deals-dialog {
  max-width: 1180px;
}
.dq-del-dialog {
  max-width: 540px;
}
@media (max-width: 992px) {
  .dq-deals-dialog,
  .dq-del-dialog {
    max-width: 96%;
  }
}

/* Modal shell */
.dq-modal-content {
  border-radius: 22px;
  border: none;
  overflow: hidden;
  box-shadow: 0 10px 25px rgba(15, 23, 42, 0.08),
    0 40px 90px rgba(15, 23, 42, 0.14);
}
.dq-modal-header {
  padding: 18px 22px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  background: linear-gradient(180deg, rgba(16, 185, 129, 0.14), #fff);
}
.dq-danger-head {
  background: linear-gradient(180deg, rgba(239, 68, 68, 0.12), #fff);
}

.dq-modal-body {
  padding: 18px 22px;
  max-height: calc(100vh - 230px);
  overflow: auto; /* ✅ scroll within modal */
}

.dq-modal-footer {
  padding: 14px 22px;
  border-top: 1px solid rgba(15, 23, 42, 0.06);
  background: #fff;
  position: sticky;
  bottom: 0;
  z-index: 2;
}

/* Header */
.dq-titlewrap {
  display: flex;
  align-items: center;
  gap: 12px;
}
.dq-title {
  font-weight: 950;
  letter-spacing: -0.35px;
}
.dq-subtitle {
  font-size: 0.82rem;
  color: rgba(15, 23, 42, 0.6);
}
.dq-head-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}
.dq-close {
  opacity: 0.9;
}
.dq-close:hover {
  opacity: 1;
}

/* Hero */
.dq-hero {
  width: 46px;
  height: 46px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}
.dq-hero-emerald {
  background: rgba(16, 185, 129, 0.16);
  color: rgb(5, 150, 105);
}
.dq-hero-red {
  background: rgba(239, 68, 68, 0.14);
  color: rgb(220, 38, 38);
}

/* Header pill */
.dq-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border-radius: 999px;
  padding: 8px 12px;
  background: rgba(15, 23, 42, 0.04);
  border: 1px solid rgba(15, 23, 42, 0.06);
  font-weight: 900;
}
.dq-pill-label {
  font-size: 0.78rem;
  color: rgba(15, 23, 42, 0.6);
}
.dq-pill-value {
  font-size: 0.82rem;
  color: rgba(15, 23, 42, 0.9);
}

/* Cards */
.dq-card {
  border-radius: 18px;
  border: 1px solid rgba(15, 23, 42, 0.06);
  background: #fff;
  padding: 14px;
}
.dq-card-head {
  padding: 2px 2px 10px;
}
.dq-card-title {
  font-weight: 950;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.dq-card-sub {
  margin-top: 6px;
  font-size: 0.82rem;
  color: rgba(15, 23, 42, 0.6);
}

/* Inputs */
.dq-label {
  font-size: 0.82rem;
  font-weight: 900;
}
.dq-input {
  border-radius: 14px;
  padding: 10px 12px;
  /*  transition: all 0.15s ease; */
}
.dq-input:focus {
  border-color: rgba(16, 185, 129, 0.55);
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.14);
}
.dq-textarea {
  resize: vertical;
}
.dq-help {
  margin-top: 6px;
  font-size: 0.8rem;
  color: rgba(15, 23, 42, 0.55);
}

/* Input group */
.dq-inputgroup {
  display: flex;
  align-items: center;
  border-radius: 14px;
  border: 1px solid rgba(15, 23, 42, 0.12);
  overflow: hidden;
  background: #fff;
}
.dq-addon {
  width: 44px;
  height: 42px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(15, 23, 42, 0.04);
  border-right: 1px solid rgba(15, 23, 42, 0.08);
  color: rgba(15, 23, 42, 0.7);
}
.dq-inputgroup-input {
  border: none;
  box-shadow: none !important;
  border-radius: 0;
}

/* Probability meter */
.dq-meter {
  height: 8px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.06);
  overflow: hidden;
  margin-top: 8px;
}
.dq-meter-bar {
  height: 100%;
  border-radius: 999px;
  background: rgba(16, 185, 129, 0.9);
  transition: width 0.25s ease;
}

/* Status hint */
.dq-status {
  margin-top: 10px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 14px;
  font-size: 0.82rem;
  font-weight: 800;
  border: 1px solid rgba(15, 23, 42, 0.06);
}
.dq-status-open {
  background: rgba(59, 130, 246, 0.08);
  color: rgba(30, 64, 175, 0.95);
}
.dq-status-won {
  background: rgba(16, 185, 129, 0.12);
  color: rgba(5, 150, 105, 1);
}
.dq-status-lost {
  background: rgba(239, 68, 68, 0.12);
  color: rgba(220, 38, 38, 1);
}

/* Tags */
.dq-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.dq-tagcheck {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 12px;
  border-radius: 999px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: rgba(15, 23, 42, 0.02);
  font-weight: 900;
  font-size: 0.82rem;
  cursor: pointer;
  user-select: none;
}
.dq-tagcheck input {
  margin-top: 0;
}
.dq-selected-tags {
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}
.dq-mini {
  font-size: 0.78rem;
  color: rgba(15, 23, 42, 0.55);
  font-weight: 900;
}

/* Chips */
.dq-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border-radius: 999px;
  padding: 8px 12px;
  background: rgba(15, 23, 42, 0.04);
  border: 1px solid rgba(15, 23, 42, 0.06);
  font-weight: 900;
  font-size: 0.82rem;
}

/* Visibility cards */
.dq-visibility {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-top: 12px;
}
@media (max-width: 992px) {
  .dq-visibility {
    grid-template-columns: 1fr;
  }
}
.dq-radio {
  cursor: pointer;
  user-select: none;
  display: block;
}
.dq-radio input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}
.dq-radio-box {
  display: grid;
  grid-template-columns: 28px 1fr;
  gap: 10px;
  align-items: start;
  padding: 12px;
  border-radius: 18px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: rgba(15, 23, 42, 0.02);
  transition: all 0.15s ease;
}
.dq-radio-box i {
  margin-top: 2px;
  font-size: 18px;
  color: rgba(15, 23, 42, 0.7);
}
.dq-radio-title {
  font-weight: 950;
}
.dq-radio-sub {
  font-size: 0.82rem;
  color: rgba(15, 23, 42, 0.6);
  margin-top: 4px;
}
.dq-radio input:checked + .dq-radio-box {
  border-color: rgba(16, 185, 129, 0.35);
  background: rgba(16, 185, 129, 0.08);
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.12);
}
.dq-radio input:checked + .dq-radio-box i {
  color: rgba(5, 150, 105, 1);
}

/* Restricted people picker */
.dq-restrict {
  border-radius: 18px;
  border: 1px solid rgba(15, 23, 42, 0.06);
  background: rgba(15, 23, 42, 0.02);
  padding: 12px;
}
.dq-restrict-head {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 10px;
}
.dq-restrict-title {
  font-weight: 950;
  display: inline-flex;
  gap: 8px;
  align-items: center;
}
.dq-restrict-sub {
  font-size: 0.82rem;
  color: rgba(15, 23, 42, 0.6);
}
.dq-people {
  max-height: 260px; /* ✅ same behavior you had */
  overflow: auto;
  background: #fff;
  border-radius: 14px;
  border: 1px solid rgba(15, 23, 42, 0.06);
}
.dq-person {
  padding: 10px 12px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
}
.dq-person:last-child {
  border-bottom: none;
}
.dq-person-left {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  cursor: pointer;
  user-select: none;
  margin: 0;
}
.dq-person-meta {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}
.dq-person-name {
  font-weight: 950;
  font-size: 0.88rem;
}
.dq-person-email {
  font-size: 0.78rem;
  color: rgba(15, 23, 42, 0.6);
}
.dq-selected {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

/* Buttons */
.dq-btn {
  border-radius: 12px;
  padding: 10px 16px;
  font-weight: 900;
}
.dq-btn-muted {
  background: rgba(15, 23, 42, 0.04);
  border: 1px solid rgba(15, 23, 42, 0.06);
}
.dq-btn-primary {
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.1);
}
.dq-btn-danger {
  box-shadow: 0 12px 24px rgba(239, 68, 68, 0.12);
}

/* Delete warning */
.dq-danger {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 14px 14px;
  border-radius: 18px;
  border: 1px solid rgba(239, 68, 68, 0.14);
  background: rgba(239, 68, 68, 0.08);
}
.dq-danger i {
  font-size: 18px;
  color: rgb(220, 38, 38);
  margin-top: 2px;
}
.dq-danger-title {
  font-weight: 950;
}
.dq-danger-sub {
  margin-top: 6px;
  font-size: 0.82rem;
  color: rgba(15, 23, 42, 0.7);
}

/* Dropdown list area */
.search-list {
  max-height: 240px;
  overflow: auto;
  border-radius: 12px;
}

/* Better dropdown width consistency in modals */
.dropdown-menu.w-100 {
  min-width: 100% !important;
}

/* optional: nicer dropdown items */
.dropdown-item {
  border-radius: 10px;
}
.dropdown-item:active {
  background: rgba(16, 185, 129, 0.12);
}

/* Reusable dropdown list */
.search-list {
  max-height: 260px;
  overflow: auto;
  border-radius: 12px;
}

/* Chip close icon */
.dq-chip i.ti-x {
  cursor: pointer;
  opacity: 0.7;
}
.dq-chip i.ti-x:hover {
  opacity: 1;
}
</style>
